<script setup>
// [TODO] This componet is too big and bulky to be in the same file, we can consider splitting this into multiple
// composables and components, useVirtualChatList, useChatlistFilters
import {
  ref,
  unref,
  provide,
  computed,
  watch,
  onMounted,
  defineEmits,
} from 'vue';
import { useStore } from 'vuex';
import { useRoute, useRouter } from 'vue-router';
import {
  useMapGetter,
  useFunctionGetter,
} from 'dashboard/composables/store.js';

import { Virtualizer } from 'virtua/vue';
import ChatListHeader from './ChatListHeader.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import ConversationFilter from 'next/filter/ConversationFilter.vue';
import SaveCustomView from 'next/filter/SaveCustomView.vue';
import ChatTypeTabs from './widgets/ChatTypeTabs.vue';
import ConversationSortRow from './widgets/conversation/ConversationSortRow.vue';
import ComposeConversation from 'dashboard/components-next/NewConversation/ComposeConversation.vue';
import CreateNewContactDialog from 'dashboard/components-next/Contacts/ContactsForm/CreateNewContactDialog.vue';
import ConversationFilterDrawer from './widgets/conversation/ConversationFilterDrawer.vue';
import ConversationItem from './ConversationItem.vue';
import { criarChegadasNaLista } from './chegadasNaLista';
import DeleteCustomViews from 'dashboard/routes/dashboard/customviews/DeleteCustomViews.vue';
import ConversationBulkActions from './widgets/conversation/conversationBulkActions/Index.vue';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import IntersectionObserver from 'dashboard/components/IntersectionObserver.vue';
import EstadoVazio from 'dashboard/components-next/EstadoVazio.vue';
import EsqueletoConversas from 'dashboard/components/widgets/conversation/EsqueletoConversas.vue';
import ConversationResolveAttributesModal from 'dashboard/components-next/ConversationWorkflow/ConversationResolveAttributesModal.vue';

import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAlert } from 'dashboard/composables';
import { useChatListKeyboardEvents } from 'dashboard/composables/chatlist/useChatListKeyboardEvents';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions';
import { useFilter } from 'shared/composables/useFilter';
import { useConversationSearch } from 'dashboard/composables/useConversationSearch';
import { useI18n } from 'vue-i18n';
import {
  useCamelCase,
  useSnakeCase,
} from 'dashboard/composables/useTransformKeys';
import { useEmitter } from 'dashboard/composables/emitter';
import { useConversationRequiredAttributes } from 'dashboard/composables/useConversationRequiredAttributes';

import { emitter } from 'shared/helpers/mitt';

import wootConstants from 'dashboard/constants/globals';
import advancedFilterOptions from './widgets/conversation/advancedFilterItems';
import filterQueryGenerator from '../helper/filterQueryGenerator.js';
import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import { generateValuesForEditCustomViews } from 'dashboard/helper/customViewsHelper';
import { conversationListPageURL } from '../helper/URLHelper';
import {
  isOnMentionsView,
  isOnParticipatingView,
  isOnUnattendedView,
} from '../store/modules/conversations/helpers/actionHelpers';
import {
  getUserPermissions,
  filterItemsByPermission,
} from 'dashboard/helper/permissionsHelper.js';
import { matchesFilters } from '../store/modules/conversations/helpers/filterHelpers';
import { dispatchWithAlert } from '../helper/dispatchWithAlert';
import { ASSIGNEE_TYPE_TAB_PERMISSIONS } from 'dashboard/constants/permissions.js';
import { nomeDaEtiqueta } from 'dashboard/helper/nomeDaEtiqueta';

const props = defineProps({
  conversationInbox: { type: [String, Number], default: 0 },
  teamId: { type: [String, Number], default: 0 },
  label: { type: String, default: '' },
  conversationType: { type: String, default: '' },
  foldersId: { type: [String, Number], default: 0 },
  showConversationList: { default: true, type: Boolean },
  isOnExpandedLayout: { default: false, type: Boolean },
});

const emit = defineEmits(['conversationLoad']);
const { uiSettings } = useUISettings();
const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const store = useStore();

const resolveAttributesModalRef = ref(null);
const conversationListRef = ref(null);
const virtualListRef = ref(null);

provide('contextMenuElementTarget', virtualListRef);

// "Criar novo" — estilo Digisac: só na aba Todos (equivalente à aba
// Contatos de lá, conforme confirmado com o Victor 2026-07-15).
const createNewContactDialogRef = ref(null);
const openCreateNewContactDialog = () => {
  createNewContactDialogRef.value?.dialogRef.open();
};
const onCreateContact = async contact => {
  try {
    await store.dispatch('contacts/create', contact);
    createNewContactDialogRef.value?.onSuccess();
    useAlert(
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SUCCESS_MESSAGE')
    );
  } catch (error) {
    useAlert(
      error?.message ||
        t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ERROR_MESSAGE')
    );
  }
};

const activeAssigneeTab = ref(wootConstants.ASSIGNEE_TYPE.ME);
const activeStatus = ref(wootConstants.STATUS_TYPE.OPEN);
const activeSortBy = ref(wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC);
// Memória de sort por aba — cada aba preserva a última escolha do usuário.
// Todas abrem em "Mais recentes" (Victor, 28/10/2026: a Fila abria em "Maior
// tempo de espera" e parecia que ele tinha mudado sem querer). Quem quiser a
// espera escolhe no seletor.
const sortByTab = ref({
  [wootConstants.ASSIGNEE_TYPE.ME]:
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  [wootConstants.ASSIGNEE_TYPE.UNASSIGNED]:
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  [wootConstants.ASSIGNEE_TYPE.BOT]:
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  [wootConstants.ASSIGNEE_TYPE.ALL]:
    wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
});
const showAdvancedFilters = ref(false);
const showFilterDrawer = ref(false);
// Filtro do drawer aplicado? (controle próprio — onApplyFilter não seta o
// getter V2, então não dá pra usar hasAppliedFilters aqui.)
const drawerFilterActive = ref(false);
// Busca mora em composable próprio: é a única parte deste arquivo com ciclo
// de vida independente, e testá-la aqui exigiria montar o ChatList inteiro.
const {
  query: localSearchQuery,
  results: searchResults,
  isLoading: isSearchLoading,
  error: searchError,
  truncated: searchTruncated,
  loadNextPage: loadNextSearchPage,
  retry: retrySearch,
} = useConversationSearch({
  assigneeType: activeAssigneeTab,
  status: activeStatus,
});
// chatsOnView is to store the chats that are currently visible on the screen,
// which mirrors the conversationList.
const chatsOnView = ref([]);
const foldersQuery = ref({});
const showAddFoldersModal = ref(false);
const showDeleteFoldersModal = ref(false);
const isContextMenuOpen = ref(false);
const appliedFilter = ref([]);
const advancedFilterTypes = ref(
  advancedFilterOptions.map(filter => ({
    ...filter,
    attributeName: t(`FILTER.ATTRIBUTES.${filter.attributeI18nKey}`),
  }))
);

const currentUser = useMapGetter('getCurrentUser');
const chatLists = useMapGetter('getFilteredConversations');
const mineChatsList = useMapGetter('getMineChats');
const allChatList = useMapGetter('getAllStatusChats');
const unAssignedChatsList = useMapGetter('getUnAssignedChats');
const botChatsList = useMapGetter('getBotChats');
const participatingChatsList = useMapGetter('getParticipatingChats');
const chatListLoading = useMapGetter('getChatListLoadingStatus');
const chatListError = useMapGetter('getChatListErrorStatus');
const activeInbox = useMapGetter('getSelectedInbox');
const conversationStats = useMapGetter('conversationStats/getStats');
const appliedFilters = useMapGetter('getAppliedConversationFiltersV2');
const folders = useMapGetter('customViews/getConversationCustomViews');
const agentList = useMapGetter('agents/getAgents');
const teamsList = useMapGetter('teams/getTeams');
const inboxesList = useMapGetter('inboxes/getInboxes');
const campaigns = useMapGetter('campaigns/getAllCampaigns');
const labels = useMapGetter('labels/getLabels');
const currentAccountId = useMapGetter('getCurrentAccountId');

// advup — filtro rápido por etiqueta (a sidebar de Etiquetas do Chatwoot foi
// removida no layout; este dropdown no header repõe o acesso).
const showLabelsMenu = ref(false);
const sidebarLabels = computed(() =>
  (labels.value || []).filter(label => label.show_on_sidebar)
);
// openLabelFilter is defined after onDrawerApply (below) to satisfy no-use-before-define
// We can't useFunctionGetter here since it needs to be called on setup?
const getTeamFn = useMapGetter('teams/getTeam');
const getConversationById = useMapGetter('getConversationById');

useChatListKeyboardEvents(conversationListRef);
const {
  selectedConversations,
  selectedInboxes,
  selectConversation,
  deSelectConversation,
  selectAllConversations,
  resetBulkActions,
  isConversationSelected,
  onAssignAgent,
  onAssignLabels,
  onRemoveLabels,
  onAssignTeamsForBulk,
  onUpdateConversations,
} = useBulkActions();

const {
  initializeStatusAndAssigneeFilterToModal,
  initializeInboxTeamAndLabelFilterToModal,
} = useFilter({
  filteri18nKey: 'FILTER',
  attributeModel: 'conversation_attribute',
});

const { checkMissingAttributes } = useConversationRequiredAttributes();

// computed

const hasAppliedFilters = computed(() => {
  return appliedFilters.value.length !== 0;
});

const activeFolder = computed(() => {
  if (props.foldersId) {
    const activeView = folders.value.filter(
      view => view.id === Number(props.foldersId)
    );
    const [firstValue] = activeView;
    return firstValue;
  }
  return undefined;
});

const activeFolderName = computed(() => {
  return activeFolder.value?.name;
});

const hasActiveFolders = computed(() => {
  return Boolean(activeFolder.value && props.foldersId !== 0);
});

const hasAppliedFiltersOrActiveFolders = computed(() => {
  return hasAppliedFilters.value || hasActiveFolders.value;
});

const currentUserDetails = computed(() => {
  const { id, name } = currentUser.value;
  return { id, name };
});

const userPermissions = computed(() => {
  return getUserPermissions(currentUser.value, currentAccountId.value);
});

const assigneeTabItems = computed(() => {
  const isSearching = localSearchQuery.value.trim().length > 0;
  return filterItemsByPermission(
    ASSIGNEE_TYPE_TAB_PERMISSIONS,
    userPermissions.value,
    item => item.permissions
  ).map(({ key, count: countKey }) => ({
    key,
    name: t(`CHAT_LIST.ASSIGNEE_TYPE_TABS.${key}`),
    count:
      isSearching && key === activeAssigneeTab.value
        ? searchResults.value.length
        : conversationStats.value[countKey] || 0,
  }));
});

const showAssigneeInConversationCard = computed(() => {
  return (
    hasAppliedFiltersOrActiveFolders.value ||
    activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ALL
  );
});

const currentPageFilterKey = computed(() => {
  return hasAppliedFiltersOrActiveFolders.value
    ? 'appliedFilters'
    : activeAssigneeTab.value;
});

const inbox = useFunctionGetter('inboxes/getInbox', activeInbox);
const currentPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  activeAssigneeTab
);
const currentFiltersPage = useFunctionGetter(
  'conversationPage/getCurrentPageFilter',
  currentPageFilterKey
);
const hasCurrentPageEndReached = useFunctionGetter(
  'conversationPage/getHasEndReached',
  currentPageFilterKey
);

const conversationCustomAttributes = useFunctionGetter(
  'attributes/getAttributesByModel',
  'conversation_attribute'
);

const activeAssigneeTabCount = computed(() => {
  // `find` pode não achar: a lista de abas é filtrada por permissão
  // (ASSIGNEE_TYPE_TAB_PERMISSIONS), então um perfil sem acesso à aba ativa
  // devolvia undefined e derrubava o componente inteiro no `.count`.
  return (
    assigneeTabItems.value.find(item => item.key === activeAssigneeTab.value)
      ?.count ?? 0
  );
});

const conversationListPagination = computed(() => {
  const conversationsPerPage = 25;
  const hasChatsOnView =
    chatsOnView.value &&
    Array.isArray(chatsOnView.value) &&
    !chatsOnView.value.length;
  const isNoFiltersOrFoldersAndChatListNotEmpty =
    !hasAppliedFiltersOrActiveFolders.value && hasChatsOnView;
  const isUnderPerPage =
    chatsOnView.value.length < conversationsPerPage &&
    activeAssigneeTabCount.value < conversationsPerPage &&
    activeAssigneeTabCount.value > chatsOnView.value.length;

  if (isNoFiltersOrFoldersAndChatListNotEmpty && isUnderPerPage) {
    return 1;
  }

  return currentPage.value + 1;
});

const conversationFilters = computed(() => {
  return {
    inboxId: props.conversationInbox ? props.conversationInbox : undefined,
    assigneeType: activeAssigneeTab.value,
    status: activeStatus.value,
    sortBy: activeSortBy.value,
    page: conversationListPagination.value,
    labels: props.label ? [props.label] : undefined,
    teamId: props.teamId || undefined,
    conversationType: props.conversationType || undefined,
  };
});

const activeTeam = computed(() => {
  if (props.teamId) {
    return getTeamFn.value(props.teamId);
  }
  return {};
});

const pageTitle = computed(() => {
  if (hasAppliedFilters.value) {
    return t('CHAT_LIST.TAB_HEADING');
  }
  if (inbox.value.name) {
    return inbox.value.name;
  }
  if (activeTeam.value.name) {
    return activeTeam.value.name;
  }
  if (props.label) {
    return `#${props.label}`;
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.MENTION) {
    return t('CHAT_LIST.MENTION_HEADING');
  }
  if (
    props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
  ) {
    return t('CONVERSATION_PARTICIPANTS.SIDEBAR_MENU_TITLE');
  }
  if (props.conversationType === wootConstants.CONVERSATION_TYPE.UNATTENDED) {
    return t('CHAT_LIST.UNATTENDED_HEADING');
  }
  if (hasActiveFolders.value) {
    return activeFolder.value.name;
  }
  return t('CHAT_LIST.TAB_HEADING');
});

function filterByAssigneeTab(conversations) {
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.ME) {
    return conversations.filter(
      c => c.meta?.assignee?.id === currentUser.value?.id
    );
  }
  if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.UNASSIGNED) {
    return conversations.filter(c => !c.meta?.assignee);
  }
  return [...conversations];
}

const conversationList = computed(() => {
  // When search active, use backend results (avoids infinite scroll pagination loop)
  if (localSearchQuery.value.trim()) {
    return searchResults.value;
  }

  let localConversationList = [];

  if (!hasAppliedFiltersOrActiveFolders.value) {
    const filters = conversationFilters.value;
    if (
      props.conversationType === wootConstants.CONVERSATION_TYPE.PARTICIPATING
    ) {
      localConversationList = filterByAssigneeTab(
        participatingChatsList.value(filters)
      );
    } else if (activeAssigneeTab.value === 'me') {
      localConversationList = [...mineChatsList.value(filters)];
    } else if (activeAssigneeTab.value === 'unassigned') {
      localConversationList = [...unAssignedChatsList.value(filters)];
    } else if (activeAssigneeTab.value === wootConstants.ASSIGNEE_TYPE.BOT) {
      localConversationList = [...botChatsList.value(filters)];
    } else {
      localConversationList = [...allChatList.value(filters)];
    }
  } else {
    localConversationList = [...chatLists.value];
  }

  if (activeFolder.value) {
    const { payload } = activeFolder.value.query;
    localConversationList = localConversationList.filter(conversation => {
      return matchesFilters(conversation, payload);
    });
  }

  return localConversationList;
});

// 🔴 PRIMEIRA PÁGINA A CAMINHO (16/09/2026, relato do A&T).
// Ao abrir "Todos", apareciam na hora as conversas de "Minhas" que já
// estavam na memória — soltas no topo, fora de ordem — e a lista de verdade
// chegava depois, empurrando tudo. Antes disso, a troca de aba mostrava a
// lista da aba ANTERIOR como "prévia". Enquanto a primeira página da aba não
// chega, o que aparece é o esqueleto dos cartões.
const carregandoPrimeiraPagina = computed(
  () =>
    chatListLoading.value &&
    !currentFiltersPage.value &&
    !localSearchQuery.value.trim()
);

const displayList = computed(() =>
  carregandoPrimeiraPagina.value ? [] : conversationList.value
);

// A conversa que chega ao topo desliza e acende (24/10/2026) — ver chegadasNaLista.js.
// A chave é tudo o que troca a lista inteira sem que nada tenha chegado.
const { listaComSaidas } = criarChegadasNaLista(
  displayList,
  computed(() =>
    [
      activeAssigneeTab.value,
      activeStatus.value,
      props.label,
      props.teamId,
      props.conversationType,
      props.foldersId,
      localSearchQuery.value,
      currentFiltersPage.value,
    ].join('|')
  )
);

const showEndOfListMessage = computed(() => {
  return (
    conversationList.value.length &&
    hasCurrentPageEndReached.value &&
    !chatListLoading.value
  );
});

const allConversationsSelected = computed(() => {
  return (
    conversationList.value.length === selectedConversations.value.length &&
    conversationList.value.every(el =>
      selectedConversations.value.includes(el.id)
    )
  );
});

const uniqueInboxes = computed(() => {
  return [...new Set(selectedInboxes.value)];
});

// ---------------------- Methods -----------------------
function setFiltersFromUISettings() {
  const { conversations_filter_by: filterBy = {} } = uiSettings.value;
  const { status, order_by: orderBy } = filterBy;
  activeStatus.value = status || wootConstants.STATUS_TYPE.OPEN;
  activeSortBy.value = Object.values(wootConstants.SORT_BY_TYPE).includes(
    orderBy
  )
    ? orderBy
    : wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC;
}

function emitConversationLoaded() {
  emit('conversationLoad');
}

function fetchFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: filterQueryGenerator(payload),
      page,
      sortBy: activeSortBy.value,
    })
    .then(emitConversationLoaded);

  showAdvancedFilters.value = false;
}

function fetchSavedFilteredConversations(payload) {
  payload = useSnakeCase(payload);
  let page = currentFiltersPage.value + 1;
  store
    .dispatch('fetchFilteredConversations', {
      queryData: payload,
      page,
      sortBy: activeSortBy.value,
    })
    .then(emitConversationLoaded);
}

function onApplyFilter(payload) {
  payload = useSnakeCase(payload);
  resetBulkActions();
  foldersQuery.value = filterQueryGenerator(payload);
  store.dispatch('conversationPage/reset');
  store.dispatch('emptyAllConversations');
  fetchFilteredConversations(payload);
}

function closeAdvanceFiltersModal() {
  showAdvancedFilters.value = false;
  appliedFilter.value = [];
}

function onUpdateSavedFilter(payload, folderName) {
  const transformedPayload = useSnakeCase(payload);
  const payloadData = {
    ...unref(activeFolder),
    name: unref(folderName),
    query: filterQueryGenerator(transformedPayload),
  };
  // A ação repassa a falha: sem este aviso, a pasta "salva" continuava com a
  // consulta antiga e ninguém ficava sabendo.
  store.dispatch('customViews/update', payloadData).catch(() => {
    useAlert(t('FILTER.CUSTOM_VIEWS.EDIT.API_FOLDERS.ERROR_MESSAGE'));
  });
  closeAdvanceFiltersModal();
}

function onClickOpenAddFoldersModal() {
  showAddFoldersModal.value = true;
}

function onCloseAddFoldersModal() {
  showAddFoldersModal.value = false;
}

function onClickOpenDeleteFoldersModal() {
  showDeleteFoldersModal.value = true;
}

function onCloseDeleteFoldersModal() {
  showDeleteFoldersModal.value = false;
}

function setParamsForEditFolderModal() {
  // Here we are setting the params for edit folder modal to show the existing values.

  // For agent, team, inboxes,and campaigns we get only the id's from the query.
  // So we are mapping the id's to the actual values.

  // For labels we get the name of the label from the query.
  // If we delete the label from the label list then we will not be able to show the label name.

  // For custom attributes we get only attribute key.
  // So we are mapping it to find the input type of the attribute to show in the edit folder modal.
  return {
    agents: agentList.value,
    teams: teamsList.value,
    inboxes: inboxesList.value,
    labels: labels.value,
    campaigns: campaigns.value,
    languages: languages,
    countries: countries,
    priority: [
      { id: 'low', name: t('CONVERSATION.PRIORITY.OPTIONS.LOW') },
      { id: 'medium', name: t('CONVERSATION.PRIORITY.OPTIONS.MEDIUM') },
      { id: 'high', name: t('CONVERSATION.PRIORITY.OPTIONS.HIGH') },
      { id: 'urgent', name: t('CONVERSATION.PRIORITY.OPTIONS.URGENT') },
    ],
    filterTypes: advancedFilterTypes.value,
    allCustomAttributes: conversationCustomAttributes.value,
  };
}

function initializeExistingFilterToModal() {
  const statusFilter = initializeStatusAndAssigneeFilterToModal(
    activeStatus.value,
    currentUserDetails.value,
    activeAssigneeTab.value
  );
  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  if (statusFilter) {
    appliedFilter.value = [...appliedFilter.value, useCamelCase(statusFilter)];
  }

  // TODO: Remove the usage of useCamelCase after migrating useFilter to camelcase
  const otherFilters = initializeInboxTeamAndLabelFilterToModal(
    props.conversationInbox,
    inbox.value,
    props.teamId,
    activeTeam.value,
    props.label
  ).map(useCamelCase);

  appliedFilter.value = [...appliedFilter.value, ...otherFilters];
}

function initializeFolderToFilterModal(newActiveFolder) {
  // Here we are setting the params for edit folder modal.
  //  To show the existing values. when we click on edit folder button.

  // Here we get the query from the active folder.
  // And we are mapping the query to the actual values.
  // To show in the edit folder modal by the help of generateValuesForEditCustomViews helper.
  const query = unref(newActiveFolder)?.query?.payload;
  if (!Array.isArray(query)) return;

  const newFilters = query.map(filter => {
    const transformed = useCamelCase(filter);
    const values = Array.isArray(transformed.values)
      ? generateValuesForEditCustomViews(
          useSnakeCase(filter),
          setParamsForEditFolderModal()
        )
      : [];

    return {
      attributeKey: transformed.attributeKey,
      attributeModel: transformed.attributeModel,
      customAttributeType: transformed.customAttributeType,
      filterOperator: transformed.filterOperator,
      queryOperator: transformed.queryOperator ?? 'and',
      values,
    };
  });

  appliedFilter.value = [...appliedFilter.value, ...newFilters];
}

function initalizeAppliedFiltersToModal() {
  appliedFilter.value = [...appliedFilters.value];
}

function onToggleAdvanceFiltersModal() {
  if (showAdvancedFilters.value === true) {
    closeAdvanceFiltersModal();
    return;
  }

  if (!hasAppliedFilters.value && !hasActiveFolders.value) {
    initializeExistingFilterToModal();
  }
  if (hasActiveFolders.value) {
    initializeFolderToFilterModal(activeFolder.value);
  }
  if (hasAppliedFilters.value) {
    initalizeAppliedFiltersToModal();
  }

  showAdvancedFilters.value = true;
}

function fetchConversations() {
  store.dispatch('updateChatListFilters', conversationFilters.value);
  store.dispatch('fetchAllConversations').then(emitConversationLoaded);
}

function resetAndFetchData() {
  appliedFilter.value = [];
  resetBulkActions();
  store.dispatch('conversationPage/reset');
  store.dispatch('clearConversationFilters');
  if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  }
  if (props.foldersId) {
    return;
  }
  fetchConversations();
}

function onCloseAdvanceFiltersModal() {
  closeAdvanceFiltersModal();
  if (!hasAppliedFilters.value) {
    resetAndFetchData();
  }
}

function onDrawerApply(payload) {
  showFilterDrawer.value = false;
  if (payload && payload.length) {
    drawerFilterActive.value = true;
    // Set appliedFilters so: (1) WebSocket addConversation/updateConversation
    // guards respect the active filter, (2) conversationList routes through
    // getFilteredConversations which applies matchesFilters client-side.
    store.dispatch('setConversationFilters', payload);
    onApplyFilter(payload);
  }
}
function onDrawerClear() {
  showFilterDrawer.value = false;
  drawerFilterActive.value = false;
  resetAndFetchData();
}
function openLabelFilter(labelTitle) {
  showLabelsMenu.value = false;
  onDrawerApply([
    {
      attribute_key: 'labels',
      filter_operator: 'equal_to',
      values: [labelTitle],
      query_operator: 'and',
      custom_attribute_type: '',
    },
  ]);
}
function onDrawerOpenAdvanced() {
  showFilterDrawer.value = false;
  onToggleAdvanceFiltersModal();
}

function loadMoreConversations() {
  // A busca também pagina. Antes esta função saía aqui e o `page: 1` fixo na
  // API deixava a busca presa nos 25 primeiros resultados, sem aviso nenhum.
  if (localSearchQuery.value.trim()) {
    loadNextSearchPage();
    return;
  }
  if (hasCurrentPageEndReached.value || chatListLoading.value) {
    return;
  }

  if (!hasAppliedFiltersOrActiveFolders.value) {
    fetchConversations();
  } else if (hasActiveFolders.value) {
    const payload = activeFolder.value.query;
    fetchSavedFilteredConversations(payload);
  } else if (hasAppliedFilters.value) {
    fetchFilteredConversations(appliedFilters.value);
  }
}

// Use IntersectionObserver instead of @scroll since Virtualizer only emits on user scroll.
// If the list doesn’t fill the viewport, loading can stall.
// IntersectionObserver triggers as soon as the sentinel is visible.
const intersectionObserverOptions = computed(() => ({
  root: conversationListRef.value,
  rootMargin: '100px 0px 100px 0px',
}));

function updateAssigneeTab(selectedTab) {
  if (activeAssigneeTab.value !== selectedTab) {
    resetBulkActions();
    emitter.emit('clearSearchInput');
    activeAssigneeTab.value = selectedTab;
    if (selectedTab === wootConstants.ASSIGNEE_TYPE.ALL) {
      activeStatus.value = wootConstants.STATUS_TYPE.ALL;
    } else if (activeStatus.value === wootConstants.STATUS_TYPE.ALL) {
      activeStatus.value = wootConstants.STATUS_TYPE.OPEN;
    }
    // Restaura o sort que o usuário escolheu nesta aba (persiste por aba).
    const desiredSort = sortByTab.value[selectedTab];
    if (activeSortBy.value !== desiredSort) {
      activeSortBy.value = desiredSort;
      store.dispatch('setChatSortFilter', desiredSort);
    }
    // Always reset page and refetch on cross-tab switch.
    // Without this, same-sort switches (e.g. Todos→Minhas) skip the fetch when
    // currentPage > 0, causing the IntersectionObserver to request page=N+1 instead
    // of page=1 and leaving the list stuck in an empty/stale state.
    if (drawerFilterActive.value && appliedFilters.value.length) {
      // Drawer filter active: re-apply filter to new tab instead of resetting
      store.dispatch('conversationPage/reset');
      store.dispatch('emptyAllConversations');
      fetchFilteredConversations(appliedFilters.value);
    } else {
      resetAndFetchData();
    }
  }
}

function onBasicFilterChange(value, type) {
  if (type === 'status') {
    activeStatus.value = value;
  } else {
    activeSortBy.value = value;
    sortByTab.value[activeAssigneeTab.value] = value;
  }
  // Com filtro do drawer ativo, `resetAndFetchData` NÃO serve: ele chama
  // `clearConversationFilters` e refaz a busca sem filtro nenhum. Mudar a
  // ordenação (ou o status) apagava, em silêncio, o filtro que o usuário
  // tinha acabado de montar — o ícone continuava aceso e a lista já era
  // outra. Mesmo caminho que a troca de aba usa logo abaixo.
  if (drawerFilterActive.value && appliedFilters.value.length) {
    store.dispatch('conversationPage/reset');
    store.dispatch('emptyAllConversations');
    fetchFilteredConversations(appliedFilters.value);
    return;
  }
  resetAndFetchData();
}

function openLastSavedItemInFolder() {
  const lastItemOfFolder = folders.value[folders.value.length - 1];
  const lastItemId = lastItemOfFolder.id;
  router.push({
    name: 'folder_conversations',
    params: { id: lastItemId },
  });
}

function openLastItemAfterDeleteInFolder() {
  if (folders.value.length > 0) {
    openLastSavedItemInFolder();
  } else {
    router.push({ name: 'home' });
    fetchConversations();
  }
}

function redirectToConversationList() {
  const {
    params: { accountId, inbox_id: inboxId, label, teamId },
    name,
  } = route;

  let conversationType = '';
  if (isOnMentionsView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.MENTION;
  } else if (isOnParticipatingView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.PARTICIPATING;
  } else if (isOnUnattendedView({ route: { name } })) {
    conversationType = wootConstants.CONVERSATION_TYPE.UNATTENDED;
  }
  router.push(
    conversationListPageURL({
      accountId,
      conversationType: conversationType,
      customViewId: props.foldersId,
      inboxId,
      label,
      teamId,
    })
  );
}

async function markAsUnread(conversationId) {
  const { ok } = await dispatchWithAlert(
    store.dispatch('markMessagesUnread', { id: conversationId })
  );
  if (ok) redirectToConversationList();
}
async function markAsRead(conversationId) {
  await dispatchWithAlert(
    store.dispatch('markMessagesRead', { id: conversationId })
  );
}

async function onAssignTeam(team, conversationId = null) {
  await dispatchWithAlert(
    store.dispatch('assignTeam', { conversationId, teamId: team.id }),
    {
      success: t(
        'CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.SUCCESFUL',
        {
          team: team.name,
          conversationId,
        }
      ),
      failure: t('CONVERSATION.CARD_CONTEXT_MENU.API.TEAM_ASSIGNMENT.FAILED'),
    }
  );
}

function toggleConversationStatus(
  conversationId,
  status,
  snoozedUntil,
  customAttributes = null
) {
  const payload = {
    conversationId,
    status,
    snoozedUntil,
  };

  if (customAttributes) {
    payload.customAttributes = customAttributes;
  }

  dispatchWithAlert(store.dispatch('toggleStatus', payload), {
    success: t('CONVERSATION.CHANGE_STATUS'),
    failure: t('CONVERSATION.CHANGE_STATUS_FAILED'),
  });
}

function handleResolveConversation(conversationId, status, snoozedUntil) {
  if (status !== wootConstants.STATUS_TYPE.RESOLVED) {
    toggleConversationStatus(conversationId, status, snoozedUntil);
    return;
  }

  // Check for required attributes before resolving
  const conversation = getConversationById.value(conversationId);
  const currentCustomAttributes = conversation?.custom_attributes || {};
  const { hasMissing, missing } = checkMissingAttributes(
    currentCustomAttributes
  );

  if (hasMissing) {
    // Pass conversation context through the modal's API
    const conversationContext = {
      id: conversationId,
      snoozedUntil,
    };
    resolveAttributesModalRef.value?.open(
      missing,
      currentCustomAttributes,
      conversationContext
    );
  } else {
    toggleConversationStatus(conversationId, status, snoozedUntil);
  }
}

function handleResolveWithAttributes({ attributes, context }) {
  if (context) {
    const existingConversation = getConversationById.value(context.id);
    const currentCustomAttributes =
      existingConversation?.custom_attributes || {};
    const mergedAttributes = { ...currentCustomAttributes, ...attributes };

    toggleConversationStatus(
      context.id,
      wootConstants.STATUS_TYPE.RESOLVED,
      context.snoozedUntil,
      mergedAttributes
    );
  }
}

function allSelectedConversationsStatus(status) {
  if (!selectedConversations.value.length) return false;
  return selectedConversations.value.every(item => {
    return getConversationById.value(item)?.status === status;
  });
}

function onContextMenuToggle(state) {
  isContextMenuOpen.value = state;
}

function toggleSelectAll(check) {
  selectAllConversations(check, conversationList);
}

useEmitter('fetch_conversation_stats', () => {
  if (hasAppliedFiltersOrActiveFolders.value) return;
  store.dispatch('conversationStats/get', conversationFilters.value);
});

onMounted(() => {
  store.dispatch('setChatListFilters', conversationFilters.value);
  setFiltersFromUISettings();
  store.dispatch('setChatStatusFilter', activeStatus.value);
  store.dispatch('setChatSortFilter', activeSortBy.value);
  resetAndFetchData();
  if (hasActiveFolders.value) {
    store.dispatch('campaigns/get');
  }
});

const deleteConversationDialogRef = ref(null);
const selectedConversationId = ref(null);

async function deleteConversation() {
  try {
    await store.dispatch('deleteConversation', selectedConversationId.value);
    redirectToConversationList();
    selectedConversationId.value = null;
    deleteConversationDialogRef.value.close();
    useAlert(t('CONVERSATION.SUCCESS_DELETE_CONVERSATION'));
  } catch (error) {
    useAlert(t('CONVERSATION.FAIL_DELETE_CONVERSATION'));
  }
}

const handleDelete = conversationId => {
  selectedConversationId.value = conversationId;
  deleteConversationDialogRef.value.open();
};

provide('selectConversation', selectConversation);
provide('deSelectConversation', deSelectConversation);
provide('assignAgent', onAssignAgent);
provide('assignTeam', onAssignTeam);
provide('assignLabels', onAssignLabels);
provide('removeLabels', onRemoveLabels);
provide('updateConversationStatus', handleResolveConversation);
provide('toggleContextMenu', onContextMenuToggle);
provide('markAsUnread', markAsUnread);
provide('markAsRead', markAsRead);
provide('isConversationSelected', isConversationSelected);
provide('deleteConversation', handleDelete);

watch(activeTeam, () => resetAndFetchData());

watch(
  computed(() => props.conversationInbox),
  () => resetAndFetchData()
);
watch(
  computed(() => props.label),
  () => resetAndFetchData()
);
watch(
  computed(() => props.conversationType),
  () => resetAndFetchData()
);

watch(activeFolder, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('customViews/setActiveConversationFolder', newVal || null);
  }
  resetAndFetchData();
});

watch(chatLists, () => {
  chatsOnView.value = conversationList.value;
});

watch(conversationFilters, (newVal, oldVal) => {
  if (newVal !== oldVal) {
    store.dispatch('updateChatListFilters', newVal);
  }
});
</script>

<template>
  <!-- Largura da coluna (17/09/2026): em tela larga ela cresce — 400px em
       1280, 460px em 1536, 520px a partir de 1800 (perto da do WhatsApp
       Web) — para caber "Dra. Jullya" no cartão sem cortar o nome do
       cliente. Em tela estreita fica como estava (ver D1 no plano: a
       conversa é quem perde espaço). -->
  <div
    class="flex flex-col min-w-0 shrink conversations-list-wrap bg-n-surface-2"
    :class="[
      { hidden: !showConversationList },
      isOnExpandedLayout
        ? 'basis-full w-full'
        : 'w-full sm:w-[300px] lg:w-[340px] xl:w-[400px] 2xl:w-[460px] min-[1800px]:w-[520px]',
    ]"
  >
    <slot />
    <ChatListHeader
      :page-title="pageTitle"
      :has-applied-filters="hasAppliedFilters"
      :has-active-folders="hasActiveFolders"
      :active-status="activeStatus"
      :is-on-expanded-layout="isOnExpandedLayout"
      :conversation-stats="conversationStats"
      :is-list-loading="chatListLoading && !displayList.length"
      @add-folders="onClickOpenAddFoldersModal"
      @delete-folders="onClickOpenDeleteFoldersModal"
      @filters-modal="onToggleAdvanceFiltersModal"
      @reset-filters="resetAndFetchData"
      @basic-filter-change="onBasicFilterChange"
    />
    <ConversationFilterDrawer
      :show="showFilterDrawer"
      :has-active-filters="drawerFilterActive"
      @close="showFilterDrawer = false"
      @apply="onDrawerApply"
      @clear="onDrawerClear"
      @open-advanced="onDrawerOpenAdvanced"
    />

    <TeleportWithDirection
      v-if="showAddFoldersModal"
      to="#saveFilterTeleportTarget"
    >
      <SaveCustomView
        v-model="appliedFilter"
        :custom-views-query="foldersQuery"
        :open-last-saved-item="openLastSavedItemInFolder"
        @close="onCloseAddFoldersModal"
      />
    </TeleportWithDirection>

    <DeleteCustomViews
      v-if="showDeleteFoldersModal"
      v-model:show="showDeleteFoldersModal"
      :active-custom-view="activeFolder"
      :custom-views-id="foldersId"
      :open-last-item-after-delete="openLastItemAfterDeleteInFolder"
      @close="onCloseDeleteFoldersModal"
    />

    <div
      class="flex items-center gap-2 px-3 py-2.5 bg-n-slate-1 border-b border-n-weak"
    >
      <!-- Busca da lista: a casca do produto, a mesma da lista de Processos,
           da de Casos e das Publicacoes. O botao de limpar era VERMELHO
           (`text-n-ruby-9 bg-n-ruby-3`) — rubi no produto significa destruir,
           e limpar um campo de busca nao destroi nada; usar a cor de perigo
           num gesto inofensivo e o que faz ninguem acreditar nela quando algo
           for mesmo perigoso. -->
      <div class="advup-busca flex-1 min-w-0">
        <i class="advup-busca__lupa i-lucide-search" aria-hidden="true" />
        <input
          v-model="localSearchQuery"
          type="text"
          :placeholder="$t('CONVERSATION.SEARCH_CONVERSATIONS')"
          class="advup-busca__campo reset-base"
        />
        <button
          v-if="localSearchQuery"
          type="button"
          class="advup-busca__limpar"
          :aria-label="$t('CONVERSATION.SEARCH_CONVERSATIONS')"
          @click="localSearchQuery = ''"
        >
          <i class="i-lucide-x" />
        </button>
      </div>
      <button
        v-tooltip.top="$t('FILTER.TOOLTIP_LABEL')"
        :aria-label="$t('FILTER.TOOLTIP_LABEL')"
        class="shrink-0 size-9 inline-flex items-center justify-center rounded-full hover:bg-n-alpha-1 transition-colors"
        :class="
          drawerFilterActive ? 'text-n-blue-11 bg-n-alpha-1' : 'text-n-slate-11'
        "
        @click="showFilterDrawer = true"
      >
        <span class="i-lucide-list-filter size-[18px]" />
      </button>
      <div class="relative shrink-0">
        <button
          v-tooltip.top="t('CHAT_LIST.LABEL_FILTER.TITLE')"
          :aria-label="t('CHAT_LIST.LABEL_FILTER.TITLE')"
          :aria-expanded="showLabelsMenu"
          class="size-9 inline-flex items-center justify-center rounded-full hover:bg-n-alpha-1 transition-colors text-n-slate-11"
          @click="showLabelsMenu = !showLabelsMenu"
        >
          <span class="i-lucide-tag size-[18px]" />
        </button>
        <div
          v-if="showLabelsMenu"
          class="fixed inset-0 z-10"
          @click="showLabelsMenu = false"
        />
        <!-- O flutuante é a peça do produto (`.advup-menu.advup-pop`), a mesma
             da busca global e do chat interno: era uma caixa com raio, sombra
             e itens próprios. -->
        <Transition name="advup-pop">
          <div v-if="showLabelsMenu" class="advup-menu advup-pop cl-etq-menu">
            <p class="advup-rotulo advup-rotulo--secao cl-etq-menu__tit">
              {{ t('CHAT_LIST.LABEL_FILTER.TITLE') }}
            </p>
            <button
              v-if="props.label"
              type="button"
              class="advup-menu__item"
              @click="openLabelFilter(props.label)"
            >
              <span class="i-lucide-x cl-etq__ico" aria-hidden="true" />
              {{ t('CHAT_LIST.LABEL_FILTER.CLEAR') }}
            </button>
            <button
              v-for="label in sidebarLabels"
              :key="label.id"
              type="button"
              class="advup-menu__item"
              :class="{ 'cl-etq--on': label.title === props.label }"
              @click="openLabelFilter(label.title)"
            >
              <span
                class="cl-etq__ponto"
                :style="{ backgroundColor: label.color }"
                aria-hidden="true"
              />
              <span class="truncate">{{ nomeDaEtiqueta(label.title) }}</span>
            </button>
            <p
              v-if="!sidebarLabels.length"
              class="advup-nota cl-etq-menu__vazio"
            >
              {{ t('CHAT_LIST.LABEL_FILTER.EMPTY') }}
            </p>
          </div>
        </Transition>
      </div>
    </div>
    <ChatTypeTabs
      v-if="!hasAppliedFiltersOrActiveFolders || drawerFilterActive"
      :items="assigneeTabItems"
      :active-tab="activeAssigneeTab"
      is-compact
      @chat-tab-change="updateAssigneeTab"
    />
    <!-- "Criar novo" — só na aba Todos (equivalente à aba Contatos do
         Digisac, confirmado com o Victor em 15/07). Eram um "Criar novo ▾"
         que abria um menu com as duas opções: dois botões diretos poupam o
         clique e dizem o que fazem sem abrir nada. -->
    <div
      v-if="
        (!hasAppliedFiltersOrActiveFolders || drawerFilterActive) &&
        activeAssigneeTab === wootConstants.ASSIGNEE_TYPE.ALL
      "
      class="cl-novo"
    >
      <ComposeConversation is-modal class="cl-novo__item">
        <template #trigger="{ toggle }">
          <button
            type="button"
            class="advup-btn advup-btn--compacto cl-novo__btn"
            @click="toggle"
          >
            <i class="i-lucide-message-square-plus" aria-hidden="true" />
            {{ t('CHAT_LIST.CREATE_NEW.NEW_CONVERSATION') }}
          </button>
        </template>
      </ComposeConversation>
      <button
        type="button"
        class="advup-btn advup-btn--compacto cl-novo__btn"
        @click="openCreateNewContactDialog"
      >
        <i class="i-lucide-user-round-plus" aria-hidden="true" />
        {{ t('CHAT_LIST.CREATE_NEW.NEW_CONTACT') }}
      </button>
    </div>
    <CreateNewContactDialog
      ref="createNewContactDialogRef"
      @create="onCreateContact"
    />
    <ConversationSortRow
      v-if="!hasAppliedFiltersOrActiveFolders || drawerFilterActive"
      @change="onBasicFilterChange"
    />
    <div
      v-if="drawerFilterActive"
      class="flex items-center justify-between gap-2 px-3 py-2 bg-n-alpha-1 border-b border-n-weak"
    >
      <span
        class="inline-flex items-center gap-1.5 text-xs font-medium text-n-blue-11 min-w-0"
      >
        <span class="i-lucide-list-filter size-3.5 shrink-0" />
        <span class="truncate">{{ $t('CHAT_LIST.FILTER_DRAWER.ACTIVE') }}</span>
      </span>
      <button
        class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium text-n-slate-11 hover:bg-n-alpha-2 transition-colors shrink-0"
        @click="onDrawerClear"
      >
        <span class="i-lucide-x size-3.5" />
        {{ $t('CHAT_LIST.FILTER_DRAWER.CLEAR') }}
      </button>
    </div>

    <!-- Busca que falhou não pode parecer "não achei nada": o catch antigo
         zerava a lista e caía no mesmo vazio de sempre. -->
    <EstadoVazio
      v-if="searchError && !conversationList.length"
      class="cl-vazio"
      tom="erro"
      icone="i-lucide-cloud-off"
      :titulo="t('CHAT_LIST.SEARCH.ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="retrySearch"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('CHAT_LIST.LIST.RETRY') }}
      </button>
    </EstadoVazio>
    <EstadoVazio
      v-else-if="chatListError && !conversationList.length"
      class="cl-vazio"
      tom="erro"
      icone="i-lucide-cloud-off"
      :titulo="t('CHAT_LIST.LIST.ERROR')"
    >
      <button
        type="button"
        class="advup-btn advup-btn--compacto"
        @click="resetAndFetchData"
      >
        <i class="i-lucide-rotate-cw" aria-hidden="true" />
        {{ t('CHAT_LIST.LIST.RETRY') }}
      </button>
    </EstadoVazio>
    <EstadoVazio
      v-else-if="
        !chatListLoading && !isSearchLoading && !conversationList.length
      "
      class="cl-vazio"
      icone="i-lucide-messages-square"
      :titulo="t('CHAT_LIST.LIST.404')"
      :dica="t('CHAT_LIST.LIST.EMPTY_HINT')"
    />
    <!-- Desmarcar a última: a barra recolhe pela altura (24/10/2026), em vez de sumir seca. -->
    <Transition name="advup-altura">
      <ConversationBulkActions
        v-if="selectedConversations.length"
        :conversations="selectedConversations"
        :all-conversations-selected="allConversationsSelected"
        :selected-inboxes="uniqueInboxes"
        :show-open-action="allSelectedConversationsStatus('open')"
        :show-resolved-action="allSelectedConversationsStatus('resolved')"
        :show-snoozed-action="allSelectedConversationsStatus('snoozed')"
        @select-all-conversations="toggleSelectAll"
        @assign-agent="onAssignAgent"
        @update-conversations="onUpdateConversations"
        @assign-labels="onAssignLabels"
        @assign-team="onAssignTeamsForBulk"
      />
    </Transition>
    <div
      ref="conversationListRef"
      class="flex-1 min-h-0 overflow-y-auto conversations-list"
      :class="{ '!overflow-hidden': isContextMenuOpen }"
    >
      <EsqueletoConversas v-if="carregandoPrimeiraPagina" />
      <Virtualizer
        ref="virtualListRef"
        v-slot="{ item, index }"
        :data="listaComSaidas"
      >
        <ConversationItem
          :source="item"
          :label="label"
          :team-id="teamId"
          :folders-id="foldersId"
          :conversation-type="conversationType"
          :show-assignee="showAssigneeInConversationCard"
          :data-index="index"
          @select-conversation="selectConversation"
          @de-select-conversation="deSelectConversation"
        />
      </Virtualizer>
      <div
        v-if="(chatListLoading && !carregandoPrimeiraPagina) || isSearchLoading"
        class="flex justify-center my-4"
      >
        <Spinner class="text-n-blue-11" />
      </div>
      <!-- "Fim da lista" saiu a pedido do Victor (16/09/2026: "retira isso,
           deixa sem nada"). No fim, o observador continua fora: senão ele
           pediria mais conversas a uma lista que já acabou.
           Sem observer quando a última busca falhou: ele dispararia
           `loadMoreConversations` em loop contra um endpoint que já está
           errando. Retentativa é decisão do usuário (botão "tentar de novo"). -->
      <IntersectionObserver
        v-else-if="!showEndOfListMessage && !chatListError && !searchError"
        :options="intersectionObserverOptions"
        @observed="loadMoreConversations"
      />
      <!-- O backend corta o casamento por conteúdo num teto. Dizer isso é o
           que separa "acabou" de "tem mais, refine". -->
      <p
        v-if="searchTruncated && !isSearchLoading"
        class="px-4 py-3 text-xs text-center text-n-slate-10"
      >
        {{ $t('CHAT_LIST.SEARCH.TRUNCATED') }}
      </p>
    </div>
    <Dialog
      ref="deleteConversationDialogRef"
      type="alert"
      :title="
        $t('CONVERSATION.DELETE_CONVERSATION.TITLE', {
          conversationId: selectedConversationId,
        })
      "
      :description="$t('CONVERSATION.DELETE_CONVERSATION.DESCRIPTION')"
      :confirm-button-label="$t('CONVERSATION.DELETE_CONVERSATION.CONFIRM')"
      @confirm="deleteConversation"
      @close="selectedConversationId = null"
    />
    <TeleportWithDirection
      v-if="showAdvancedFilters"
      to="#conversationFilterTeleportTarget"
    >
      <ConversationFilter
        v-model="appliedFilter"
        :folder-name="activeFolderName"
        :is-folder-view="hasActiveFolders"
        @apply-filter="onApplyFilter"
        @update-folder="onUpdateSavedFilter"
        @close="onCloseAdvanceFiltersModal"
      />
    </TeleportWithDirection>
    <ConversationResolveAttributesModal
      ref="resolveAttributesModalRef"
      @submit="handleResolveWithAttributes"
    />
  </div>
</template>

<style scoped>
/* 🔴 A LISTA CEDE ESPAÇO ANTES DA CONVERSA (16/09/2026).
 *
 * Ela era `shrink-0` com 340px fixos, e a ficha do contato tinha 320px de
 * mínimo: em 820px de largura sobravam 108px para a CONVERSA, com a bolha da
 * mensagem em 60px quebrando o texto letra a letra. As duas laterais eram
 * rígidas e só o meio encolhia — a única coisa que a tela existe para mostrar
 * era a que sumia.
 *
 * Agora a ordem de quem cede é explícita: primeiro a ficha vira gaveta
 * (ConversationSidebar), depois a lista encolhe até o piso onde o cartão
 * ainda se lê, e a conversa nunca recebe menos que o mínimo dela
 * (ConversationBox). O piso é 288px: medido na tela, é onde as abas
 * "Minhas / Fila / Todos" ainda cabem com o nome inteiro e o cartão mostra
 * avatar, nome e uma linha de prévia. Em 264px os rótulos viravam "M." e
 * "F..", que é abreviação que ninguém entende. */
.conversations-list-wrap {
  min-width: 288px;
}

.cl-etq-menu {
  position: absolute;
  z-index: 20;
  top: 44px;
  inset-inline-end: 0;
  overflow-y: auto;
  width: 13rem;
  max-height: 18rem;
}

.cl-etq-menu__tit {
  padding: var(--esp-1) var(--esp-3);
  margin: 0;
}

.cl-etq-menu__vazio {
  padding: var(--esp-2) var(--esp-3);
  margin: 0;
}

.cl-etq__ico {
  width: var(--ico-1);
  height: var(--ico-1);
  flex-shrink: 0;
}

/* O ponto é a cor da etiqueta — o dado, não enfeite. */
.cl-etq__ponto {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  border-radius: var(--r-redondo);
}

.cl-etq--on {
  font-weight: var(--peso-forte);
}

.cl-vazio {
  padding: var(--esp-6) var(--esp-4);
}

.cl-novo {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--esp-2);
  padding: var(--esp-2) var(--esp-3);
  border-bottom: 1px solid rgb(var(--slate-3));
}

/* Os dois dividem a coluna; o nome encolhe antes de quebrar a fileira. */
.cl-novo__btn,
.cl-novo__item {
  min-width: 0;
}

.cl-novo__btn {
  width: 100%;
  overflow: hidden;
  white-space: nowrap;
}
</style>
